"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import {
  HiPaperAirplane,
  HiArrowRight,
  HiChatAlt2,
  HiChevronDown,
} from "react-icons/hi";
import { CgSpinner } from "react-icons/cg";
import Link from "next/link";
import { useLanguage, useTranslations } from "@/lib/i18n";

export default function Contact() {
  const { language } = useLanguage();
  const t = useTranslations();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: language === "id" 
      ? "Informasi Ketersediaan Kamar" 
      : "Room Availability Information",
    message: "",
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    setTimeout(() => {
      const phoneNumber = "6281916401702";
      const greeting = language === "id" 
        ? "*Halo KinKen Concierge Team,* 👋\nSaya mengirim pesan melalui website KinKen Kost Elite."
        : "*Hello KinKen Concierge Team,* 👋\nI'm sending a message through the KinKen Kost Elite website.";

      const text = `
${greeting}

📝 *${language === "id" ? "Detail Pengirim" : "Sender Details"}:*
${language === "id" ? "Nama" : "Name"}: ${formData.name}
Email: ${formData.email}

📌 *${language === "id" ? "Subjek" : "Subject"}:*
${formData.subject}

💬 *${language === "id" ? "Pesan" : "Message"}:*
${formData.message}

${language === "id" ? "Mohon responsnya, terima kasih." : "Please respond, thank you."}
      `.trim();

      window.open(
        `https://wa.me/${phoneNumber}?text=${encodeURIComponent(text)}`,
        "_blank",
      );

      setIsLoading(false);
    }, 1000);
  };

  return (
    <section
      id="contact"
      className="py-24 md:py-32 bg-white relative overflow-hidden"
    >
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* LEFT COLUMN: VISUAL */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative h-[600px] w-full rounded-sm overflow-hidden shadow-2xl">
              <Image
                src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?q=80&w=2058&auto=format&fit=crop"
                alt="KinKen Concierge Service"
                fill
                className="object-cover"
                priority
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent flex flex-col justify-end p-10 md:p-14">
                <div className="mb-6">
                  <span className="text-brand-gold text-xs font-bold tracking-[0.25em] uppercase block mb-2 font-sans">
                    {t.contact.tagline}
                  </span>
                  <h3 className="font-serif text-3xl md:text-4xl text-white mb-4">
                    {t.contact.title}
                  </h3>
                  <p className="text-gray-200 font-light text-sm md:text-base leading-relaxed mb-6 font-sans">
                    {t.contact.description}
                  </p>
                </div>

                {/* BUTTON LINK */}
                <div className="mb-8">
                  <Link href="/contact">
                    <Button
                      variant="outline"
                      className="rounded-none bg-transparent border-white text-white hover:bg-white hover:text-brand-charcoal px-6 py-6 uppercase text-xs font-bold tracking-widest transition-all duration-300 flex items-center gap-2 font-sans group"
                    >
                      {t.contact.detailsButton}
                      <HiArrowRight
                        size={16}
                        className="group-hover:translate-x-1 transition-transform"
                      />
                    </Button>
                  </Link>
                </div>

                <div className="flex items-center gap-4 text-white border-t border-white/20 pt-6">
                  <div className="w-12 h-12 rounded-full bg-brand-gold flex items-center justify-center shrink-0">
                    <HiChatAlt2 size={20} />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider opacity-70 font-sans">
                      {t.contact.fastResponse}
                    </p>
                    <p className="font-serif text-xl">+62 819 1640 1702</p>
                    <p className="text-xs text-gray-300 mt-1 font-sans">
                      WhatsApp • 24/7 • {language === "id" ? "Dwibahasa" : "Bilingual"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute -top-10 -left-10 w-40 h-40 border-[1px] border-brand-gold/30 -z-10 hidden md:block"></div>
          </motion.div>

          {/* RIGHT COLUMN: FORM */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-[#FAFAFA] p-8 md:p-12 border border-gray-100"
          >
            <span className="text-brand-gold text-xs font-bold tracking-[0.25em] uppercase block mb-6 font-sans">
              {t.contact.formTagline}
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-brand-charcoal mb-4">
              {t.contact.formTitle}
            </h2>
            <p className="text-gray-500 font-light text-sm mb-8 font-sans">
              {t.contact.formSubtitle}
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500 font-sans">
                    {t.contact.nameLabel}
                  </label>
                  <input
                    required
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-white border border-gray-200 p-4 text-sm focus:outline-none focus:border-brand-gold transition-colors font-sans"
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500 font-sans">
                    Email
                  </label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-white border border-gray-200 p-4 text-sm focus:outline-none focus:border-brand-gold transition-colors font-sans"
                    placeholder="email@example.com"
                  />
                </div>
              </div>

              <div className="space-y-2 relative">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500 font-sans">
                  {t.contact.subjectLabel}
                </label>
                <div className="relative">
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full bg-white border border-gray-200 p-4 text-sm focus:outline-none focus:border-brand-gold transition-colors text-gray-600 font-sans appearance-none pr-10"
                  >
                    {t.contact.subjects.map((subject, index) => (
                      <option key={index}>{subject}</option>
                    ))}
                  </select>
                  <HiChevronDown
                    size={16}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500 font-sans">
                  {t.contact.messageLabel}
                </label>
                <textarea
                  required
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="w-full bg-white border border-gray-200 p-4 text-sm focus:outline-none focus:border-brand-gold transition-colors resize-none font-sans"
                  placeholder={t.contact.messagePlaceholder}
                ></textarea>
              </div>

              <Button
                disabled={isLoading}
                className="w-full bg-brand-charcoal hover:bg-brand-gold text-white py-4 md:py-6 rounded-none uppercase text-[10px] md:text-xs font-bold tracking-[0.15em] md:tracking-[0.2em] transition-all duration-500 flex gap-2 items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed font-sans group"
              >
                {isLoading ? (
                  <>
                    {t.contact.processing} <CgSpinner size={16} className="animate-spin" />
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">{t.contact.sendButton}</span>
                    <span className="sm:hidden">{t.contact.sendButtonShort}</span>
                    <HiPaperAirplane
                      size={16}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-400 text-center font-sans">
                {t.contact.disclaimer}
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
